import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Cpu, Plug, CheckCircle2, XCircle, Loader2, Trash2, RefreshCw, Star } from "lucide-react";

export default function ProviderSettings() {
  const [providers, setProviders] = useState([]);
  const [active, setActive] = useState({ provider: "ollama", model: "llama3" });
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [results, setResults] = useState({});

  const load = useCallback(async () => {
    const d = await api.listProviders();
    setProviders(d.providers);
    if (d.active?.provider) setActive({ provider: d.active.provider, model: d.active.model });
    setDrafts((prev) => {
      const next = { ...prev };
      d.providers.forEach((p) => {
        if (!next[p.provider]) next[p.provider] = { api_key: "", base_url: p.base_url, model: p.default_models[0] };
      });
      return next;
    });
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const setDraft = (p, k, v) => setDrafts((d) => ({ ...d, [p]: { ...d[p], [k]: v } }));

  const save = async (p) => {
    setBusy(`save-${p.provider}`);
    try {
      await api.saveProvider({
        provider: p.provider,
        api_key: drafts[p.provider]?.api_key || "",
        base_url: p.custom_base ? drafts[p.provider]?.base_url : null,
      });
      setDraft(p.provider, "api_key", "");
      toast.success(`${p.label} saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(""); }
  };

  const test = async (p) => {
    setBusy(`test-${p.provider}`);
    setResults((r) => ({ ...r, [p.provider]: null }));
    try {
      const r = await api.testProvider(p.provider, drafts[p.provider]?.model || p.default_models[0]);
      setResults((s) => ({ ...s, [p.provider]: r }));
      r.ok ? toast.success(`${p.label} responded in ${r.latency_ms}ms`) : toast.error(r.error);
    } finally { setBusy(""); }
  };

  const loadModels = async (p) => {
    setBusy(`models-${p.provider}`);
    try {
      const r = await api.providerModels(p.provider);
      setProviders((list) => list.map((x) => x.provider === p.provider ? { ...x, default_models: r.models } : x));
      toast[r.live ? "success" : "warning"](r.live ? `${r.models.length} models loaded` : (r.error || "Using built-in list"));
    } finally { setBusy(""); }
  };

  const remove = async (p) => {
    await api.deleteProvider(p.provider);
    toast.success(`${p.label} key removed`);
    load();
  };

  const makeActive = async (p, model) => {
    const next = { provider: p.provider, model: model || drafts[p.provider]?.model || p.default_models[0] };
    await api.setActiveLlm(next);
    setActive(next);
    toast.success(`Synthesizer will use ${p.label} · ${next.model}`);
  };

  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-5" data-testid="provider-settings">
      <div className="flex items-center gap-2 flex-wrap">
        <Cpu className="h-4 w-4 text-cyan-400" />
        <h3 className="font-display font-bold text-base">AI Providers — bring your own key</h3>
        <span data-testid="active-provider-badge"
          className="ml-auto px-2 py-0.5 rounded font-mono text-[10px] uppercase border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
          active: {active.provider} · {active.model}
        </span>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Keys are encrypted on the server and never sent back to your browser. Local Ollama / LM Studio need no key.
      </p>

      <div className="space-y-3">
        {providers.map((p) => {
          const d = drafts[p.provider] || {};
          const res = results[p.provider];
          const isActive = active.provider === p.provider;
          return (
            <div key={p.provider} data-testid={`provider-card-${p.provider}`}
              className={cn("rounded-md border p-4 space-y-3",
                isActive ? "border-cyan-500/40 bg-cyan-500/[0.04]" : "border-border bg-[#0E1420]")}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm text-slate-200">{p.label}</span>
                <span className={cn("px-1.5 py-0.5 rounded font-mono text-[9px] uppercase border",
                  p.key_saved ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : p.needs_key ? "border-slate-600 bg-slate-700/20 text-slate-400"
                      : "border-purple-500/40 bg-purple-500/10 text-purple-300")}>
                  {p.key_saved ? "key saved" : p.needs_key ? "no key" : "no key needed"}
                </span>
                {isActive && <Star className="h-3.5 w-3.5 text-cyan-400 fill-cyan-400" />}
                <div className="ml-auto flex items-center gap-1">
                  <Button size="sm" variant="ghost" data-testid={`provider-models-${p.provider}`}
                    onClick={() => loadModels(p)} disabled={busy === `models-${p.provider}`}
                    className="h-7 text-slate-400 hover:text-cyan-300">
                    {busy === `models-${p.provider}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    <span className="ml-1 font-mono text-[10px]">models</span>
                  </Button>
                  {p.key_saved && (
                    <Button size="sm" variant="ghost" data-testid={`provider-delete-${p.provider}`} onClick={() => remove(p)}
                      className="h-7 text-slate-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                {p.needs_key && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-slate-400">API key</Label>
                    <Input data-testid={`provider-key-${p.provider}`} type="password" autoComplete="off"
                      value={d.api_key || ""} onChange={(e) => setDraft(p.provider, "api_key", e.target.value)}
                      placeholder={p.key_saved ? "•••••••• saved — blank keeps it" : "paste your key"}
                      className="bg-[#0B0F18] border-border font-mono text-sm" />
                  </div>
                )}
                {p.custom_base && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-slate-400">Endpoint</Label>
                    <Input data-testid={`provider-base-${p.provider}`} value={d.base_url || ""}
                      onChange={(e) => setDraft(p.provider, "base_url", e.target.value)}
                      className="bg-[#0B0F18] border-border font-mono text-sm" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-slate-400">Model</Label>
                  <Input data-testid={`provider-model-${p.provider}`} list={`models-${p.provider}`}
                    value={d.model || ""} onChange={(e) => setDraft(p.provider, "model", e.target.value)}
                    className="bg-[#0B0F18] border-border font-mono text-sm" />
                  <datalist id={`models-${p.provider}`}>
                    {p.default_models.map((m) => <option key={m} value={m} />)}
                  </datalist>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" data-testid={`provider-save-${p.provider}`} onClick={() => save(p)}
                  disabled={busy === `save-${p.provider}`}
                  className="h-8 bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold">Save</Button>
                <Button size="sm" variant="outline" data-testid={`provider-test-${p.provider}`} onClick={() => test(p)}
                  disabled={busy === `test-${p.provider}`} className="h-8 border-border hover:bg-slate-800">
                  {busy === `test-${p.provider}` ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plug className="h-3.5 w-3.5 mr-1" />}
                  Test
                </Button>
                <Button size="sm" data-testid={`provider-activate-${p.provider}`} onClick={() => makeActive(p)}
                  disabled={isActive} className="h-8 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold disabled:opacity-40">
                  {isActive ? "In use" : "Use for synthesis"}
                </Button>
                {res && (
                  <span data-testid={`provider-result-${p.provider}`}
                    className={cn("flex items-center gap-1.5 font-mono text-[11px]", res.ok ? "text-emerald-300" : "text-rose-300")}>
                    {res.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    {res.ok ? `${res.latency_ms}ms · ${res.reply?.slice(0, 40)}` : res.error?.slice(0, 90)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
